/**
 * Lightweight in-game HUD. Keeps DOM writes throttled; game code just calls
 * `update` every rendered frame.
 */

/** What the shared action button is called, per prompt kind. */
const ACTION_LABELS = {
  fuel: 'Refuel',
  tea: 'Buy jabana',
  petrol: 'Fill up from the cans',
  juice: 'Buy juice',
};

export class HUD {
  constructor({ config }) {
    this.config = config;
    this.moneyElement = document.getElementById('hud-money');
    this.moneyValueElement = document.getElementById('hud-money-value');
    this.healthElement = document.getElementById('hud-health');
    this.healthFillElement = document.getElementById('hud-health-fill');
    this.healthValueElement = document.getElementById('hud-health-value');
    this.fuelElement = document.getElementById('hud-fuel');
    this.fuelFillElement = document.getElementById('hud-fuel-fill');
    this.fuelValueElement = document.getElementById('hud-fuel-value');
    this.refuelElement = document.getElementById('hud-refuel');
    this.refuelButtonElement = document.getElementById('hud-refuel-button');
    this.refuelTextElement = document.getElementById('hud-refuel-text');
    this.statsElement = document.getElementById('hud-stats');
    this.fpsElement = document.getElementById('hud-fps');
    this.bodiesElement = document.getElementById('hud-bodies');
    this.ridersElement = document.getElementById('hud-riders');
    this.ridersTextElement = document.getElementById('hud-riders-text');
    this.toastElement = document.getElementById('toast');
    this.alertElement = document.getElementById('hud-alert');
    this.alertTitleElement = document.getElementById('hud-alert-title');
    this.alertAmountElement = document.getElementById('hud-alert-amount');

    if (!config.debug.showStats) {
      if (this.statsElement) this.statsElement.hidden = true;
    }

    this._statsTimer = 0;
    this._toastTimeout = 0;
    this._alertTimeout = 0;
    this._lastMoney = -1;
    this._lastHealthPercent = -1;
    this._lastHealthFill = -1;
    this._lastHealthLow = null;
    this._lastHealthEmpty = null;
    this._lastFuelPercent = -1;
    this._lastFuelFill = -1;
    this._lastFuelLow = null;
    this._lastFuelEmpty = null;
    this._lastRefuelPrompt = null;
    this._lastPromptKind = null;
    this._lastRideStatus = null;
    this._lastRideMode = null;
  }

  update(
    dt,
    {
      fps,
      bodies,
      money = 0,
      health = 100,
      maxHealth = 100,
      fuel = 1,
      refuelPrompt = '',
      promptKind = 'fuel',
      rideStatus = '',
      rideMode = 'hail',
    },
  ) {
    // Money (Sudanese pounds, SDG) climbs continuously; throttle DOM writes to
    // whole pounds.
    const moneyValue = Math.max(0, Math.floor(money));
    if (moneyValue !== this._lastMoney) {
      this._lastMoney = moneyValue;
      if (this.moneyValueElement) {
        this.moneyValueElement.textContent = moneyValue.toLocaleString('en-US');
      }
      this.moneyElement?.setAttribute('aria-label', `Money ${moneyValue} SDG`);
    }

    // Health meter. Damage is applied in whole 5-point steps, so only write
    // when the displayed value or warning state actually changes.
    const healthMax = Math.max(1, maxHealth);
    const healthFraction = Math.min(1, Math.max(0, health / healthMax));
    const healthPercent = Math.round(healthFraction * 100);
    if (healthPercent !== this._lastHealthPercent) {
      this._lastHealthPercent = healthPercent;
      if (this.healthValueElement) this.healthValueElement.textContent = `${healthPercent}`;
      this.healthElement?.setAttribute('aria-valuenow', String(healthPercent));
      this.healthElement?.setAttribute('aria-valuemax', String(healthMax));
    }

    const healthFill = Math.round(healthFraction * 10000) / 100;
    if (healthFill !== this._lastHealthFill) {
      this._lastHealthFill = healthFill;
      if (this.healthFillElement) this.healthFillElement.style.width = `${healthFill}%`;
    }

    const healthLow =
      healthFraction <= (this.config.vehicle?.health?.lowThreshold ?? 0.3);
    const healthEmpty = healthFraction <= 0.001;
    if (healthLow !== this._lastHealthLow || healthEmpty !== this._lastHealthEmpty) {
      this._lastHealthLow = healthLow;
      this._lastHealthEmpty = healthEmpty;
      this.healthElement?.classList.toggle('low', healthLow);
      this.healthElement?.classList.toggle('empty', healthEmpty);
    }

    // Fuel gauge: update only when the rounded percentage or warning state
    // actually changes, so the HUD stays cheap.
    const fuelFraction = Math.min(1, Math.max(0, fuel));
    const fuelPercent = Math.round(fuelFraction * 100);
    if (fuelPercent !== this._lastFuelPercent) {
      this._lastFuelPercent = fuelPercent;
      if (this.fuelValueElement) this.fuelValueElement.textContent = `${fuelPercent}%`;
      this.fuelElement?.setAttribute('aria-valuenow', String(fuelPercent));
    }

    // Let the bar follow the true tank level so even small amounts of driving
    // produce visible movement, while the printed value stays a clean percent.
    const fuelFill = Math.round(fuelFraction * 10000) / 100;
    if (fuelFill !== this._lastFuelFill) {
      this._lastFuelFill = fuelFill;
      if (this.fuelFillElement) this.fuelFillElement.style.width = `${fuelFill}%`;
    }

    const fuelLow =
      fuelFraction <= (this.config.vehicle?.fuel?.lowThreshold ?? 0.2);
    const fuelEmpty = fuelFraction <= 0.001;
    if (fuelLow !== this._lastFuelLow || fuelEmpty !== this._lastFuelEmpty) {
      this._lastFuelLow = fuelLow;
      this._lastFuelEmpty = fuelEmpty;
      this.fuelElement?.classList.toggle('low', fuelLow);
      this.fuelElement?.classList.toggle('empty', fuelEmpty);
    }

    const prompt = refuelPrompt || '';
    if (prompt !== this._lastRefuelPrompt || promptKind !== this._lastPromptKind) {
      this._lastRefuelPrompt = prompt;
      this._lastPromptKind = promptKind;
      this.refuelElement?.classList.toggle('visible', Boolean(prompt));
      this.refuelElement?.classList.toggle('is-active', prompt.startsWith('Refueling'));
      // One button serves the pumps, the tea stand, the petrol stall and the
      // juice shop; the icon, accent and label follow what the truck is
      // parked beside.
      this.refuelElement?.setAttribute('data-kind', promptKind);
      this.refuelElement?.setAttribute('aria-hidden', String(!prompt));
      if (prompt && this.refuelTextElement) {
        this.refuelTextElement.textContent = prompt;
      }
      this.refuelButtonElement?.setAttribute(
        'aria-label',
        ACTION_LABELS[promptKind] ?? ACTION_LABELS.fuel,
      );
    }

    const ride = rideStatus || '';
    if (ride !== this._lastRideStatus || rideMode !== this._lastRideMode) {
      this._lastRideStatus = ride;
      this._lastRideMode = rideMode;
      this.ridersElement?.classList.toggle('visible', Boolean(ride));
      this.ridersElement?.setAttribute('aria-hidden', String(!ride));
      this.ridersElement?.setAttribute('data-mode', rideMode);
      if (this.ridersTextElement) {
        this.ridersTextElement.textContent = ride;
      }
    }

    if (!this.config.debug.showStats) return;

    // FPS and body count are developer diagnostics only.
    this._statsTimer += dt;
    if (this._statsTimer < 0.3) return;
    this._statsTimer = 0;

    if (this.fpsElement) this.fpsElement.textContent = `${fps} fps`;
    if (this.bodiesElement) this.bodiesElement.textContent = `${bodies} bodies`;
  }

  toast(message, duration = 2.2) {
    if (!this.toastElement) return;

    window.clearTimeout(this._toastTimeout);
    this.toastElement.textContent = message;
    this.toastElement.classList.add('visible');

    this._toastTimeout = window.setTimeout(() => {
      this.toastElement.classList.remove('visible');
    }, duration * 1000);
  }

  /**
   * A dedicated on-screen alert, separate from the shared toast, for events
   * that must not be missed — e.g. the dollar jumping and wiping money, or the
   * power going out. Shows a title and an amount, pulses in, and hides itself.
   */
  alert({ title = '', amount = '', duration = 4.5 } = {}) {
    if (!this.alertElement) return;

    if (this.alertTitleElement) this.alertTitleElement.textContent = title;
    if (this.alertAmountElement) this.alertAmountElement.textContent = amount;

    // Restart the entrance and pulse animation on a repeat hit.
    this.alertElement.classList.remove('visible');
    this.alertElement.setAttribute('aria-hidden', 'false');
    void this.alertElement.offsetWidth;
    this.alertElement.classList.add('visible');

    window.clearTimeout(this._alertTimeout);
    this._alertTimeout = window.setTimeout(() => {
      this.alertElement.classList.remove('visible');
      this.alertElement.setAttribute('aria-hidden', 'true');
    }, duration * 1000);
  }

  dispose() {
    window.clearTimeout(this._toastTimeout);
    window.clearTimeout(this._alertTimeout);
  }
}
